// React
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Servisni sloj
import {
  getEffectivePricingVersionAPI,
  getPricingPacksAPI,
  getPricingVersionsAPI,
} from "services/api/pricing/api";
import { PricingPack, PricingVersion, PricingVersionRow } from "services/api/pricing/types";

// Helpers
import { transformRoute } from "helpers";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { PRICING_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import PricingTabMenu from "ui/modules/pricing/components/PricingTabMenu/PricingTabMenu";
import PricingEditCard from "ui/modules/pricing/components/PricingEditCard/PricingEditCard";

// Lokalne komponente
import PricingSearchResults from "./components/PricingSearchResults/PricingSearchResults";

// Tipovi
import { PricingSearchPageCmp, PricingSearchPageProps } from "./types";

/**
 * Pretraga cjenika. Odavde se ide u pojedinu verziju i odavde se otvara nova.
 *
 * Novi cjenik kreće od verzije koja vrijedi danas: sve vrijednosti se preuzimaju, a mijenja
 * se datum početka važenja. Zato se pri otvaranju obrasca dohvaća ta verzija.
 */
const PricingSearchPage: PricingSearchPageCmp = (_props: PricingSearchPageProps) => {
  const title = PRICING_PAGE_TITLE;

  const navigate = useNavigate();

  const [versions, setVersions] = useState<PricingVersionRow[]>([]);
  const [packs, setPacks] = useState<PricingPack[]>([]);
  const [baseVersion, setBaseVersion] = useState<PricingVersion | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    setIsLoading(true);

    try {
      const [rows, allPacks] = await Promise.all([
        getPricingVersionsAPI(),
        getPricingPacksAPI(),
      ]);

      setVersions(rows);
      setPacks(allPacks);
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = useCallback(
    (versionId: string) => {
      navigate(transformRoute(Routes.PricingDetailPageRouter, { versionId }));
    },
    [navigate]
  );

  const handleNewClick = useCallback(async () => {
    setIsLoading(true);

    try {
      setBaseVersion(await getEffectivePricingVersionAPI());
      setShowEditModal(true);
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  const handleEditSuccess = useCallback(
    (saved: PricingVersion) => {
      setShowEditModal(false);

      navigate(transformRoute(Routes.PricingDetailPageRouter, { versionId: saved.id }));
    },
    [navigate]
  );

  return (
    <div>
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle title={title.text} icon={title.icon} iconColor={title.iconColor} />
              <Breadcrumb />
            </div>

            {/* Novi cjenik kreće od verzije koja vrijedi danas, pa nema smisla bez nje. */}
            {versions.length > 0 && (
              <Button
                variant="primary"
                children="Novi cjenik"
                startIcon="/media/icons/plus.svg"
                onClick={handleNewClick}
              />
            )}
          </div>
        </PageHeader>
        <PricingTabMenu />
      </div>

      <div className="p-4">
        {!isLoading && versions.length === 0 ? (
          <div className="bg-white rounded-md p-6">
            <p className="text-sm text-slate-500">
              Cjenik još nije postavljen. Prva verzija dolazi sjemenskom migracijom.
            </p>
          </div>
        ) : (
          <PricingSearchResults versions={versions} onSelect={handleSelect} />
        )}
      </div>

      {baseVersion && (
        <PricingEditCard
          show={showEditModal}
          version={baseVersion}
          /* Osnova je uvijek verzija koja vrijedi danas — s pretrage se novi cjenik radi iz nje. */
          isEffective
          packs={packs}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      )}

      <Loader isLoading={isLoading} message="Učitavanje podataka…">
        {null}
      </Loader>
    </div>
  );
};

PricingSearchPage.route = Routes.PricingPageRouter;

export default PricingSearchPage;
